/**
 * LOCO — accessibility primitives.
 * Dialogs use native <dialog>.showModal(): the browser provides the focus trap,
 * inert background and Escape handling. This file adds opener focus restoration,
 * scroll locking, animated closing and outside-click dismissal.
 */
(() => {
  const LOCO = (window.LOCO = window.LOCO || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const openers = new WeakMap();

  function lockScroll(lock) {
    document.body.classList.toggle('is-locked', lock);
  }

  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    lockScroll(true);
    const autofocus = dialog.querySelector('[autofocus]');
    if (autofocus) autofocus.focus();
    dialog.dispatchEvent(new CustomEvent('loco:dialog-open', { bubbles: true }));
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };
    if (reduceMotion.matches || !dialog.classList.contains('drawer')) {
      finish();
    } else {
      dialog.classList.add('is-closing');
      dialog.addEventListener('animationend', finish, { once: true });
      // Safety net if animationend never fires
      setTimeout(() => dialog.open && dialog.classList.contains('is-closing') && finish(), 400);
    }
  }

  // Restore focus + unlock scroll whenever any dialog closes (including via Escape)
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (!document.querySelector('dialog[open]')) lockScroll(false);
      const opener = openers.get(dialog);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
      openers.delete(dialog);
      dialog.dispatchEvent(new CustomEvent('loco:dialog-close', { bubbles: true }));
    },
    true
  );

  // Escape → animated close for drawers
  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (dialog instanceof HTMLDialogElement && dialog.classList.contains('drawer')) {
        event.preventDefault();
        closeDialog(dialog);
      }
    },
    true
  );

  // Declarative openers/closers
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop (the dialog element itself) closes it
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  // Disclosure menus (<details>): Escape closes and returns focus, outside click closes
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = event.target.closest && event.target.closest('details[open][data-disclosure]');
    if (open) {
      open.removeAttribute('open');
      open.querySelector('summary').focus();
    }
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[open][data-disclosure]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
  document.addEventListener('focusout', (event) => {
    const details = event.target.closest && event.target.closest('details[open][data-disclosure]');
    if (details && event.relatedTarget && !details.contains(event.relatedTarget)) details.removeAttribute('open');
  });

  LOCO.a11y = { openDialog, closeDialog, reduceMotion };
})();
